import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Google Analytics 4 Measurement ID
// Replace with your actual GA4 ID: G-XXXXXXXXXX
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

// Initialize GA4
export const initGA = () => {
  if (!GA_MEASUREMENT_ID) {
    return;
  }

  // Check if gtag is already loaded
  if (window.gtag) return;

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false // We'll send page views manually
  });
};

// Track page views
export const trackPageView = (path) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;
  
  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: path
  });
};

// Track events
export const trackEvent = (eventName, params = {}) => {
  if (!window.gtag || !GA_MEASUREMENT_ID) return;
  
  window.gtag('event', eventName, params);
};

// Track form submissions
export const trackFormSubmission = (formName, success = true) => {
  trackEvent('form_submission', {
    form_name: formName,
    success: success
  });
};

// Track consultation booking
export const trackConsultationBooking = (language) => {
  trackEvent('generate_lead', {
    currency: 'EUR',
    value: 0,
    event_category: 'consultation',
    event_label: `consultation_${language}`
  });
};

// Track chat interactions
export const trackChatOpen = () => {
  trackEvent('chat_open', {
    event_category: 'engagement',
    event_label: 'ai_chat'
  });
};

export const trackChatMessage = () => {
  trackEvent('chat_message', {
    event_category: 'engagement',
    event_label: 'ai_chat'
  });
};

export const trackChatTranscript = () => {
  trackEvent('chat_transcript_sent', {
    event_category: 'lead',
    event_label: 'ai_chat_handoff'
  });
};

// Track phone clicks
export const trackPhoneClick = () => {
  trackEvent('click', {
    event_category: 'contact',
    event_label: 'phone_call'
  });
};

// Track WhatsApp clicks
export const trackWhatsAppClick = () => {
  trackEvent('click', {
    event_category: 'contact',
    event_label: 'whatsapp'
  });
};

// Track CTA button clicks
export const trackCTAClick = (ctaName) => {
  trackEvent('cta_click', {
    event_category: 'engagement',
    event_label: ctaName
  });
};

// React hook for page view tracking
export const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    initGA();
  }, []);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
};

// Analytics Provider component
const GoogleAnalytics = () => {
  usePageTracking();
  return null;
};

export default GoogleAnalytics;
